// THE ONE SENDER behind §4.4's failure-action sheet, shared by BOTH hosts
// (07-B-3). See `FailureActionArm` in inspection-run-port.ts for why this is
// not two implementations: a disposition is an online act on the floor and in
// the office alike — it consumes a real subject and drives modules 04/05/06 in
// one server transaction — so the only thing connectivity changes is whether
// the controls are live, never where the write goes.
//
// NEITHER SENDER THROWS (the port rule): a refusal comes back as a
// `refused` outcome carrying the problem's own sentence, its code and the
// request id, because the refusals here are DECISIONS the screen must relay —
// `QUALITY_SCRAP_ALREADY_ATTRIBUTED` names the hand-off and the honest
// continuations, `QUALITY_REWORK_REQUIRES_HOLD_RELEASE` names the operation
// and the role set that may clear it, `QUALITY_SCRAP_CAUSE_UNMAPPED` names the
// unmapped failure cause. Swallowing any of those into a generic error toast
// would discard the exact text a qc lead acts on. A 401 is the one exception
// with a DIFFERENT shape (R2): the session is gone, so the arm navigates to
// /login — the same redirect every read path takes — instead of leaving an
// unhandled rejection under the sheet.
//
// THE IDEMPOTENCY KEY IS PER INTENT, NOT PER ATTEMPT (R1). A key minted fresh
// on every send defeats the §9.15 replay it exists for: the retry of a
// lost-response scrap would POST a SECOND scrap under a new key. So the key is
// minted when a NEW intent first sends — an intent being this subject plus
// exactly this body — is REUSED for as long as retries carry the same body and
// only transport has failed (no server decision exists yet), and is discarded
// the moment a non-transport outcome settles: a `done` or a coded refusal
// means the server SAW the intent, and the platform idempotency store would
// replay that stored answer at any later attempt under the same key, even one
// whose body the user has since fixed.

import { useCallback, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { client } from "../../../../lib/api";
import { UnauthorizedError } from "../../../../lib/problem";
import { newIdempotencyKey, unwrap, RequestError } from "../../../../lib/request";
import { useOnline } from "../../../floor-pwa/hooks/use-online";
import type { FailureActionArm, FailureActionOutcome } from "./inspection-run-port";

/** Classify a thrown request failure into the arm's `refused` shape. A
 * transport failure (no server answer at all) carries `code: undefined`,
 * which is what keeps its intent key alive for the replayed retry (R1).
 * `UnauthorizedError` never reaches this — the senders handle it first (R2). */
function refusedOf(error: unknown): FailureActionOutcome<never> {
  if (error instanceof RequestError) {
    return {
      kind: "refused",
      code: error.code,
      message: error.message,
      detail: error.problem?.detail,
      requestId: error.requestId,
    };
  }
  return {
    kind: "refused",
    code: undefined,
    message: error instanceof Error ? error.message : String(error),
    detail: undefined,
    requestId: undefined,
  };
}

/** One in-flight user intent: the body it settled on and the key that body
 * rides under, for every attempt until a non-transport outcome lands. */
interface Intent {
  readonly fingerprint: string;
  readonly key: string;
}

/** The intent's key: reused while the body is byte-identical (a replayed
 * retry), fresh the moment the body changes (a NEW intent — reusing a key
 * under a different body would make the store replay the wrong answer). */
function keyFor(intents: Map<string, Intent>, scope: string, fingerprint: string): string {
  const prior = intents.get(scope);
  if (prior?.fingerprint === fingerprint) return prior.key;
  const key = newIdempotencyKey();
  intents.set(scope, { fingerprint, key });
  return key;
}

/** Discard the intent on any settled outcome; keep it ONLY across transport
 * failures, where no server decision exists and the retry must replay. */
function settleIntent(
  intents: Map<string, Intent>,
  scope: string,
  outcome: FailureActionOutcome<unknown>,
): void {
  if (outcome.kind === "refused" && outcome.code === undefined) return;
  intents.delete(scope);
}

export function useFailureActionArm(): FailureActionArm {
  const online = useOnline();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  // Keyed by scope ("resolve:<inspection>" / "quarantine") — a ref, because an
  // intent must survive re-renders and must never trigger one.
  const intents = useRef(new Map<string, Intent>());

  /** R2 — the 401 shape: navigate to /login (the read paths' redirect) and
   * hand back a refused outcome for the frame the sheet still renders. */
  const signedOut = useCallback((): FailureActionOutcome<never> => {
    void navigate({ to: "/login" });
    return {
      kind: "refused",
      code: "UNAUTHENTICATED",
      message: t("quality.run.signedOut"),
      detail: undefined,
      requestId: undefined,
    };
  }, [navigate, t]);

  const resolve = useCallback<FailureActionArm["resolve"]>(
    async (input) => {
      setBusy(true);
      const scope = `resolve:${input.inspectionId}`;
      const key = keyFor(intents.current, scope, JSON.stringify(input.body));
      let outcome: Awaited<ReturnType<FailureActionArm["resolve"]>>;
      try {
        const res = await client.POST("/v1/quality/inspections/{id}/resolve", {
          params: {
            path: { id: input.inspectionId },
            header: { "Idempotency-Key": key },
          },
          body: input.body,
        });
        outcome = { kind: "done", result: unwrap(res, "Could not resolve the failure") };
      } catch (error) {
        outcome = error instanceof UnauthorizedError ? signedOut() : refusedOf(error);
      } finally {
        setBusy(false);
      }
      settleIntent(intents.current, scope, outcome);
      return outcome;
    },
    [signedOut],
  );

  const quarantine = useCallback<FailureActionArm["quarantine"]>(
    async (input) => {
      setBusy(true);
      const scope = "quarantine";
      const key = keyFor(intents.current, scope, JSON.stringify(input.body));
      let outcome: Awaited<ReturnType<FailureActionArm["quarantine"]>>;
      try {
        const res = await client.POST("/v1/quality/holds", {
          params: { header: { "Idempotency-Key": key } },
          body: input.body,
        });
        outcome = { kind: "done", result: unwrap(res, "Could not quarantine the stock") };
      } catch (error) {
        outcome = error instanceof UnauthorizedError ? signedOut() : refusedOf(error);
      } finally {
        setBusy(false);
      }
      settleIntent(intents.current, scope, outcome);
      return outcome;
    },
    [signedOut],
  );

  /**
   * §9.6's re-measure, driven from the sheet (07-C). MODULE 06's route, through
   * the ONE api-client (doctrine 3) — the same shape the other two senders
   * take, including the per-intent key: a lost-response re-measure retried
   * under a fresh key would post a SECOND adjustment movement for one physical
   * measurement, which is exactly the double-post R1's rule exists to stop.
   *
   * The scope is the UNIT, not the inspection: two inspections on one drum
   * asking for the same measurement are the same intent, and the fingerprint
   * (the entered length) is what distinguishes a replay from a correction.
   */
  const remeasure = useCallback<FailureActionArm["remeasure"]>(
    async (input) => {
      setBusy(true);
      const scope = `remeasure:${input.stockUnitId}`;
      const key = keyFor(intents.current, scope, JSON.stringify(input.body));
      let outcome: Awaited<ReturnType<FailureActionArm["remeasure"]>>;
      try {
        const res = await client.POST("/v1/inventory/stock-units/{id}/remeasure", {
          params: { path: { id: input.stockUnitId }, header: { "Idempotency-Key": key } },
          body: input.body,
        });
        outcome = { kind: "done", result: unwrap(res, "Could not re-measure the unit") };
      } catch (error) {
        outcome = error instanceof UnauthorizedError ? signedOut() : refusedOf(error);
      } finally {
        setBusy(false);
      }
      settleIntent(intents.current, scope, outcome);
      return outcome;
    },
    [signedOut],
  );

  /**
   * D-299 R8's preview of the rework door for the step a rework would
   * re-open. A read: no key, no intent bookkeeping and no `busy` (see the
   * port). A `null` target sends no query at all, so the door's own default
   * (the inspection's own step) answers — never an empty string, which the
   * route would refuse as a malformed uuid. A 401 takes the same redirect as
   * the senders; any other failure comes back `refused`, and the sheet decides
   * what a refusal means for its Confirm.
   */
  const reworkTarget = useCallback<FailureActionArm["reworkTarget"]>(
    async (input) => {
      try {
        const res = await client.GET("/v1/quality/inspections/{id}/rework-target", {
          params: {
            path: { id: input.inspectionId },
            query:
              input.workOrderOperationId === null
                ? {}
                : { work_order_operation_id: input.workOrderOperationId },
          },
        });
        return { kind: "done", result: unwrap(res, "Could not check that step") };
      } catch (error) {
        return error instanceof UnauthorizedError ? signedOut() : refusedOf(error);
      }
    },
    [signedOut],
  );

  return { resolve, quarantine, remeasure, reworkTarget, busy, online };
}
